import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
    const audioRef = useRef(null);
    const [current, setCurrent] = useState(null); // episode object
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0); // seconds
    const [duration, setDuration] = useState(0); // seconds
    const [volume, setVolume] = useState(0.85);
    const [endedEpisode, setEndedEpisode] = useState(null); // last fully-played episode
    const currentRef = useRef(null);

    if (!audioRef.current && typeof window !== "undefined") {
        audioRef.current = new Audio();
        audioRef.current.preload = "metadata";
    }

    useEffect(() => {
        currentRef.current = current;
    }, [current]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;
        const onTime = () => setProgress(audio.currentTime || 0);
        const onMeta = () => setDuration(audio.duration || 0);
        const onEnd = () => {
            setIsPlaying(false);
            // capture which episode just finished so listeners can celebrate
            if (currentRef.current) setEndedEpisode(currentRef.current);
        };
        audio.addEventListener("timeupdate", onTime);
        audio.addEventListener("loadedmetadata", onMeta);
        audio.addEventListener("ended", onEnd);
        return () => {
            audio.removeEventListener("timeupdate", onTime);
            audio.removeEventListener("loadedmetadata", onMeta);
            audio.removeEventListener("ended", onEnd);
        };
    }, []);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    const playEpisode = useCallback((ep) => {
        const audio = audioRef.current;
        if (!audio || !ep) return;
        if (current?.id !== ep.id) {
            audio.src = ep.audio_url;
            setCurrent(ep);
            setProgress(0);
        }
        audio.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }, [current]);

    const togglePlay = useCallback(() => {
        const audio = audioRef.current;
        if (!audio || !current) return;
        if (audio.paused) {
            audio.play().then(() => setIsPlaying(true));
        } else {
            audio.pause();
            setIsPlaying(false);
        }
    }, [current]);

    const seek = useCallback((seconds) => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = Math.min(Math.max(seconds, 0), duration || 0);
        setProgress(audio.currentTime);
    }, [duration]);

    const skip = useCallback((delta) => {
        const audio = audioRef.current;
        if (!audio) return;
        seek((audio.currentTime || 0) + delta);
    }, [seek]);

    const value = {
        current,
        isPlaying,
        progress,
        duration,
        volume,
        endedEpisode,
        clearEndedEpisode: () => setEndedEpisode(null),
        playEpisode,
        togglePlay,
        seek,
        skip,
        setVolume,
    };

    return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

export const usePlayer = () => {
    const ctx = useContext(PlayerContext);
    if (!ctx) throw new Error("usePlayer must be inside PlayerProvider");
    return ctx;
};

export const formatTime = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
};
